import { useState, useEffect, useRef, useCallback } from 'react';

// Phase 8 (Exam Prep orchestration port): a standalone countdown hook for the
// Pomodoro timer. Existing inline timers in this app (PracticeTestTaking,
// DrillPage) decrement by a fixed step per tick, which drifts if the tab is
// throttled/backgrounded for more than a tick. This one recomputes remaining
// time from a stored end-timestamp every tick, so it stays correct regardless
// of how long the interval was delayed.

export function useCountdown(onComplete?: () => void) {
  const [remainingMs, setRemainingMs] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const endTsRef = useRef<number | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const start = useCallback((durationMs: number) => {
    endTsRef.current = Date.now() + durationMs;
    setRemainingMs(durationMs);
    setIsRunning(true);
  }, []);

  const pause = useCallback(() => {
    if (endTsRef.current != null) {
      setRemainingMs(Math.max(0, endTsRef.current - Date.now()));
    }
    setIsRunning(false);
  }, []);

  const reset = useCallback(() => {
    setIsRunning(false);
    setRemainingMs(0);
    endTsRef.current = null;
  }, []);

  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => {
      const left = Math.max(0, (endTsRef.current ?? 0) - Date.now());
      setRemainingMs(left);
      if (left <= 0) {
        setIsRunning(false);
        onCompleteRef.current?.();
      }
    }, 250);
    return () => clearInterval(id);
  }, [isRunning]);

  return { remainingMs, isRunning, start, pause, reset };
}
